import * as React from "react";

import { Box, makeStyles, Theme, Typography } from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import * as Redux from "react-redux";

import { MopButton } from "client/components";
import {
  FormCollectionManager,
  FormCollectionModalModeMap,
  FormCollectionProvider,
  useFormCollection,
} from "client/domain";
import {
  UIFieldMap,
  useMerchantModel,
  useTranslation,
  useUiFieldMap,
} from "client/hooks";
import { useNormalizedBrandPaletteValues } from "client/hooks/useNormalizedBrandThemeValues";
import { FieldSets, MerchantFieldMapper } from "client/lib/apt-merchant";
import { errorActions, merchantActions, RootState } from "client/store";

import { FieldRenderer } from "../FieldRenderer";
import { FormBuilder } from "../FormBuilder";
import { StaticFieldRenderer } from "../StaticFieldRenderer";
import { YesOrNoQuestionsHeader } from "../YesOrNoQuestionsHeader";

import {
  SingleStoreFooter,
  StoreLocationsHeader,
} from "./StoreLocationsForm.components";

const useStyles = makeStyles((theme: Theme) => {
  const {
    successFg,
    formFooterBg,
    formFooterBorder,
  } = useNormalizedBrandPaletteValues(theme);

  return {
    deleteIcon: {
      cursor: "pointer",
    },
    fileUploadSuccess: {
      color: successFg,
    },
    singleLocationWrapper: {
      backgroundColor: formFooterBg,
      borderRadius: theme.shape.borderRadius,
      border: formFooterBorder,
    },
  };
});

export type StoreLocationFormUiFieldMap = MerchantFieldMapper<UIFieldMap>;

const singleLocationFieldMap: Partial<
  FieldSets.StoreLocationValueMapper<UIFieldMap>
> = {
  branchNumber: {},
  isMall: { width: 12, customFieldHeader: <YesOrNoQuestionsHeader /> },
  mallName: { width: 12 },
  isResidence: { width: 12 },
  locationName: {},
  locationStreet: { width: 12 },
  locationStreet2: {},
  locationCity: {},
  locationState: {},
  locationPostalCode: {},
};

export const storeLocationUiFieldMap: StoreLocationFormUiFieldMap = {
  locations: [singleLocationFieldMap],
};

export const getStoreLocationUiFieldMap = (
  locations: FieldSets.StoreLocation[]
): StoreLocationFormUiFieldMap => ({
  locations: locations.map((_) => singleLocationFieldMap),
});

export const StoreLocationsForm: React.FC = () => {
  const { merchant } = useMerchantModel();
  const initialState = {
    canAddToCollection: true,
    completedFieldSetCount: 1,
    collection: merchant.locations,
    currentFieldSetIndex: 0,
  };
  return (
    <FormCollectionProvider initialState={initialState}>
      <StoreLocationsFormContent />
    </FormCollectionProvider>
  );
};
type LocationState = "ManualEntry" | "SingleFileUpload";

export const StoreLocationsFormContent: React.FC = () => {
  const {
    merchant: { storeLocationsFile },
  } = Redux.useSelector(({ merchant }: RootState) => ({
    merchant,
  }));

  const [locationState, setLocationState] = React.useState<LocationState>(
    Boolean(storeLocationsFile?.path) ? "SingleFileUpload" : "ManualEntry"
  );
  const {
    addFieldSetToCollection,
    merchant,
    validateMerchant,
  } = useMerchantModel();
  const { orderedUiFieldMap, updateUiFieldMap } = useUiFieldMap(
    getStoreLocationUiFieldMap(merchant.locations)
  );

  /**
   * If we're in Manual Entry mode, we need to have the initial location
   *
   * Unlike business owners, we don't always need at least 1
   */
  React.useEffect(() => {
    if (locationState === "ManualEntry" && !merchant.locations.length) {
      addFieldSetToCollection("locations");
    }
  }, [addFieldSetToCollection, locationState, merchant.locations.length]);

  /**
   * When the merchant.locations.length changes, we want to update the orderedUiFieldMap
   */
  React.useEffect(() => {
    if (merchant.locations?.length !== orderedUiFieldMap.locations?.length) {
      updateUiFieldMap(getStoreLocationUiFieldMap(merchant.locations));
    }
  }, [
    merchant.locations,
    merchant.locations?.length,
    orderedUiFieldMap.locations?.length,
    updateUiFieldMap,
  ]);

  const {
    completedFieldSetCount,
    currentFieldSetIndex,
    handleAddToCollection,
    handleRemoveFromCollection,
    modalMode,
    setModalState,
  } = useFormCollection();

  const t = useTranslation();
  const classes = useStyles();
  const dispatch = Redux.useDispatch();

  const handleAddAnotherStoreClick = () => {
    const isValid = validateMerchant(merchant.locations[0]);
    if (isValid) {
      handleAddToCollection("locations");
    } else {
      dispatch(
        errorActions.setError(
          "You have missing or invalid store location fields"
        )
      );
    }
  };

  const EditableModalBody = () => (
    <Box mb={2}>
      <FormBuilder
        fieldSet={merchant.locations[currentFieldSetIndex]}
        onSubmit={() => setModalState("closed")}
        submitLabel={formCollectionModalMap[modalMode].primaryActionText}
        uiFieldMap={orderedUiFieldMap?.locations?.[currentFieldSetIndex]}
      />
    </Box>
  );

  const DeleteModalBody = () => (
    <Box mb={2}>
      <Box mb={2}>
        <StaticFieldRenderer
          fieldSet={merchant.locations[currentFieldSetIndex]}
          uiFieldMap={orderedUiFieldMap?.locations?.[currentFieldSetIndex]}
        />
      </Box>
      <MopButton
        mopVariant="primary"
        fullWidth
        onClick={() =>
          handleRemoveFromCollection("locations", currentFieldSetIndex)
        }
      >
        {formCollectionModalMap[modalMode].primaryActionText}
      </MopButton>
    </Box>
  );

  const getLocationName = () => {
    if (merchant.locations?.[currentFieldSetIndex]) {
      return merchant.locations[currentFieldSetIndex].locationName.value;
    }
    return "";
  };

  const formCollectionModalMap: FormCollectionModalModeMap = {
    add: {
      headerText: t("addAdditionalStoreLocationHeader"),
      modalBody: EditableModalBody,
      primaryActionText: t("add"),
    },
    delete: {
      headerText: `Are you sure you want to delete "${getLocationName()}"`,
      modalBody: DeleteModalBody,
      primaryActionText: t("uiDeleteModalConfirm"),
    },
    edit: {
      headerText: `Edit "${getLocationName()}"`,
      modalBody: EditableModalBody,
      primaryActionText: t("uiEditModalSaveChanges"),
    },
  };

  const getLabel = (idx: number): string =>
    merchant.locations?.[idx]?.locationName?.value ?? "";

  const removeFileUpload = () => {
    setLocationState("ManualEntry");
    dispatch(merchantActions.setStoreLocationUpload({ name: "", path: "" }));
  };

  if (completedFieldSetCount < 2) {
    return (
      <Box mb={2}>
        <StoreLocationsHeader />
        <Box className={classes.singleLocationWrapper} mb={3} p={2}>
          {locationState === "ManualEntry" && merchant.locations.length > 0 && (
            <>
              <Box mb={2} ml={1}>
                <Typography variant="body2">
                  <strong>{t("uiStoreLocationStore1")}</strong>
                </Typography>
              </Box>
              <Box mb={3}>
                <FieldRenderer
                  fieldSet={merchant.locations[0]}
                  uiFieldMap={orderedUiFieldMap?.locations?.[0]}
                />
              </Box>
              <SingleStoreFooter onAddClick={handleAddAnotherStoreClick} />
            </>
          )}
          {locationState === "SingleFileUpload" && (
            <Box>
              <Box
                alignItems="center"
                display="flex"
                justifyContent="space-between"
                mb={1}
              >
                <strong>{storeLocationsFile?.name}</strong>
                <DeleteIcon
                  className={classes.deleteIcon}
                  onClick={removeFileUpload}
                />
              </Box>
              <Typography className={classes.fileUploadSuccess} variant="body2">
                {t("uiUploadSuccess")}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box mb={2}>
      <StoreLocationsHeader />
      <Box mb={2}>
        <FormCollectionManager
          additionalLinkText={`+ ${t("addAdditionalStoreLocation")}`}
          allowAdditional={true}
          collection={merchant.locations}
          collectionType="locations"
          getLabel={getLabel}
          modalBody={formCollectionModalMap[modalMode].modalBody}
          modalHeader={
            <Typography variant="body2">
              <strong>{formCollectionModalMap[modalMode].headerText}</strong>
            </Typography>
          }
        />
      </Box>
    </Box>
  );
};
